import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
/** REDUX */
import { batch, useDispatch, useSelector } from 'react-redux';
import {
  clearStoreAction,
  hasNewMessagesAction,
  initialLoadAction,
  scrollToBottomAction,
  setGlobalLoaderAction,
  setHasMoreMessagesAction,
  setHighlightMessageAction,
  setMessageDecoratorAction,
  setMessagesInStoreAction,
  updateChannelAction,
  updateDirectChannelsAction,
  updateUsersAction,
} from '../../redux/actions/collaboration';
import { clearChannelNotificationsAction } from '../../redux/actions/notifications';
/** APOLLO */
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import getChannel from './apollo/getChannel';
import getMessagesBefore from './apollo/getMessagesBefore';
import getMessagesAfter from './apollo/getMessagesAfter';
import viewChannelMutation from './apollo/viewChannelMutation';
import {
  IChannelViewMutation,
  IChannelViewMutationVariables,
  IGetChannelQuery,
  IGetChannelQueryVariables,
  IGetMessagesBeforeQuery,
  IGetMessagesBeforeQueryVariables,
  UserState,
} from '../../utils/apollo/graphqlSchema';
/** COMPONENTS */
import CollaborationMenu from './CollaborationMenu';
import CollaborationLoader from './CollaboartionLoader';
import CollaborationChat from './CollaborationChat';
/** CUSTOM HOOKS */
import { useDidUpdateEffect } from '../../utils/hooks';
/** UTILS */
import { CHANNEL_UPDATE_FREQUENCY } from '../../config/constants';
import { combineTwoMessageList, getNewMessageList } from './utils';
/** CUSTOM UI COMPONENTS */
import * as CollaborationUI from './CollaborationUI';
import { updateWorkspaces } from '../../redux/slices/workspaces';
import { useLoadWorkspaceAnalyses } from '../../utils/hooks/workspaces';
import {
  IWorkspaceSliceRow,
  populateWorkspace,
} from '../../redux/slices/workspace';
import { workspacePropertySelector } from '../../redux/selectors/workspace';
import { useAppSelector } from '../../redux/hooks';
import toastMessage from '../../utils/toastMessage';

export default memo(function Collaboration() {
  const dispatch = useDispatch();
  const client = useApolloClient();

  const userProfileId = useMemo(
    () => localStorage.getItem('kubit:profile_id'),
    [],
  );
  const chatContainer = useRef<any>(null);

  if (!chatContainer.current) {
    chatContainer.current = document.getElementById('chat-container');
  }

  const workspaceId = useAppSelector((state) =>
    workspacePropertySelector(state, 'id'),
  );
  const workspaceRows: IWorkspaceSliceRow[] = useAppSelector((state) =>
    workspacePropertySelector(state, 'rows'),
  );

  const currentWorkspaceAnalysesCount = useMemo(
    () =>
      workspaceRows.reduce(
        (accumulator: number, { analysesIds }) =>
          accumulator + analysesIds.length,
        0,
      ),
    [workspaceRows],
  );

  const mainChannelId = useSelector(
    (state: any) => state.collaboration.mainChannelId,
  );
  const currentChannelId = useSelector(
    (state: any) => state.collaboration.currentChannelId,
  );
  const lastViewedAt = useSelector(
    (state: any) => state.collaboration.lastViewedAt,
  );
  const stateUserList = useSelector(
    (state: any) => state.collaboration.userList,
  );

  const loadedChannelId = useSelector(
    (state: any) => state.collaboration?.loadedChannelId,
  );

  const messages = useSelector((state: any) => state.collaboration.messages);
  const activeTag = useSelector((state: any) => state.collaboration.activeTag);

  const workspaceName = useSelector(
    (state: any) => state.collaboration?.workspaceMeta?.name,
  );
  const workspaceDirects = useSelector(
    (state: any) => state.collaboration?.workspaceMeta?.directs,
  );

  const loadWorkspaceAnalyses = useLoadWorkspaceAnalyses();

  /* View channel mutation run after every update to refresh channel */
  const [viewChannel] = useMutation<
    IChannelViewMutation,
    IChannelViewMutationVariables
  >(viewChannelMutation, {
    variables: {
      input: {
        channelId: currentChannelId,
      },
    },
  });

  const fetchMessages = useCallback(
    async (currentChannelUnreadMessages?: any) => {
      let combinedMessages: any = currentChannelUnreadMessages || [];
      let shouldScrollToBottom = true;
      /* Mark the first unread message if preset */
      const firstUnreadMessageId =
        combinedMessages.length > 0 ? combinedMessages[0].id : '-1';
      let messageId = firstUnreadMessageId;
      /* Set message decorator */
      if (firstUnreadMessageId !== '-1') {
        dispatch(
          setMessageDecoratorAction({
            name: 'newMessageSplitter',
            id: firstUnreadMessageId,
          }),
        );
      }

      /* Check for message in hash and if preset fetch all messages after it */
      if (window.location.hash && window.location.hash.includes('message')) {
        shouldScrollToBottom = false;
        const hashTokens = window.location.hash.substr(1).split('-');
        messageId = hashTokens[hashTokens.length - 1];

        window.history.replaceState({}, '', window.location.href.split('#')[0]);

        const getMessageAfterRes = await client.query({
          query: getMessagesAfter,
          variables: {
            channelId: currentChannelId,
            messageId,
          },
        });

        const oldMessages = getMessageAfterRes?.data?.messagesAfter?.messages;

        combinedMessages = combineTwoMessageList(oldMessages, combinedMessages);
      }

      /* If the already fetched messages are not enough fetch more old to fill the list.
      If the count is satisfied check the BE if there are more old messages */
      const count =
        combinedMessages.length > 19 ? 0 : 20 - combinedMessages.length;

      const getMessageBeforeRes = await client.query<
        IGetMessagesBeforeQuery,
        IGetMessagesBeforeQueryVariables
      >({
        query: getMessagesBefore,
        variables: {
          count,
          channelId: currentChannelId,
          messageId,
        },
      });

      /* Combine read and unread messages together */
      const oldMessages = getMessageBeforeRes?.data?.messagesBefore?.messages;
      const hasMoreMessages =
        getMessageBeforeRes?.data?.messagesBefore?.hasMore ?? false;

      combinedMessages = combineTwoMessageList(oldMessages, combinedMessages);
      batch(() => {
        dispatch(setMessagesInStoreAction(combinedMessages));
        dispatch(setHasMoreMessagesAction(hasMoreMessages));
        dispatch(scrollToBottomAction(shouldScrollToBottom));
        dispatch(setGlobalLoaderAction(false));
        dispatch(
          setHighlightMessageAction(!shouldScrollToBottom ? messageId : ''),
        );
      });
    },
    [dispatch, client, currentChannelId],
  );

  const onChannelFetched = useCallback(
    async ({ channel, users, workspace }: IGetChannelQuery) => {
      /* Populate channel data */
      if (!loadedChannelId || loadedChannelId !== currentChannelId) {
        batch(() => {
          dispatch(
            initialLoadAction({
              channel,
              userList:
                users.entities.length > 0
                  ? users.entities.filter((user) => user.name.length > 0)
                  : [],
              workspace,
            }),
          );
          dispatch(
            clearChannelNotificationsAction({
              workspaceId: workspace?.id,
              channelId: channel?.id,
            }),
          );
        });
        fetchMessages(channel?.messages);
      } else {
        /* Check for any new analyses and if any add them to current workspace */
        if (workspace) {
          const workspaceAnalysesCount = workspace.rows.reduce(
            (accumulator: number, { analyses }) =>
              accumulator + analyses.length,
            0,
          );

          // Analyses can be added and removed to workspace
          if (workspaceAnalysesCount !== currentWorkspaceAnalysesCount) {
            if (workspaceAnalysesCount < currentWorkspaceAnalysesCount) {
              toastMessage({
                message: 'Charts have been removed from this workspace.',
                type: 'info',
              });
            } else {
              toastMessage({
                message: 'New charts have been added to the workspace.',
                type: 'info',
              });
            }

            batch(() => {
              loadWorkspaceAnalyses(workspace.rows);

              dispatch(populateWorkspace(workspace));

              dispatch(
                updateWorkspaces({
                  id: workspace.id,
                  name: workspace.name,
                }),
              );
            });
          }

          /* Update direct channels */
          if (workspace.directs?.length > workspaceDirects.length) {
            dispatch(updateDirectChannelsAction(workspace.directs));
          }
        }

        /* Check for new users in the workspace */
        if (users?.entities?.length > stateUserList.length) {
          dispatch(updateUsersAction(users.entities));
        }

        /* Check for any new messages and load them */
        if (
          channel &&
          channel?.id === currentChannelId &&
          channel?.messages?.length > 0 &&
          activeTag === ''
        ) {
          const channelMessages = channel?.messages;

          const combinedMessages = combineTwoMessageList(
            messages,
            channelMessages,
          );

          const newMessages = getNewMessageList(messages, channelMessages);
          /* Check if own submitted message in new messages list
           if yes, scroll to bottom */
          const hasOwnMessage = newMessages.find(
            (message: any) => message.creator.id === userProfileId,
          );
          const chatContainerScrollHeight = chatContainer?.current.scrollHeight;
          const chatContainerScrolledDistance =
            chatContainer?.current.scrollTop;
          const chatContainerClientHeight = chatContainer?.current.clientHeight;
          dispatch(setMessagesInStoreAction(combinedMessages));
          if (
            hasOwnMessage ||
            chatContainerScrollHeight - chatContainerScrolledDistance ===
              chatContainerClientHeight
          ) {
            dispatch(scrollToBottomAction(true));
          } else if (
            !hasOwnMessage &&
            newMessages.length > 0 &&
            chatContainerScrollHeight !== chatContainerClientHeight
          ) {
            dispatch(hasNewMessagesAction());
          }
        }
      }

      /* Mark channel as viewed */
      const { data } = await viewChannel();
      const channelView = data?.channelView;
      /* Update loop */
      setTimeout(
        () => dispatch(updateChannelAction({ channel, channelView })),
        CHANNEL_UPDATE_FREQUENCY,
      );
    },
    [
      dispatch,
      fetchMessages,
      activeTag,
      currentWorkspaceAnalysesCount,
      messages,
      stateUserList,
      userProfileId,
      viewChannel,
      workspaceDirects,
      currentChannelId,
      loadedChannelId,
      loadWorkspaceAnalyses,
    ],
  );

  useQuery<IGetChannelQuery, IGetChannelQueryVariables>(getChannel, {
    variables: {
      workspaceId,
      channelId: currentChannelId,
      since: lastViewedAt,
      pagination: { first: 0, after: 0 },
      userStates: [UserState.Active, UserState.Pending],
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: onChannelFetched,
  });

  const getChannelNameById = useMemo(() => {
    if (mainChannelId === currentChannelId) {
      return workspaceName;
    }
    const result = workspaceDirects.find((walkChannel: any) => {
      if (walkChannel.id === currentChannelId) {
        return true;
      }
      return false;
    });

    if (result && result.users[0] && result.users[1]) {
      const profileId = localStorage.getItem('kubit:profile_id');
      return result.users[0].id !== profileId
        ? result.users[0].name
        : result.users[1].name;
    }
    return 'Loading Name...';
  }, [mainChannelId, currentChannelId, workspaceDirects, workspaceName]);

  useDidUpdateEffect(() => {
    /* SKIP ON MOUNT */
    /* Refetch messages if active tag is disabled */
    if (!activeTag) {
      fetchMessages();
    }
  }, [activeTag]);

  useEffect(() => {
    /* Clear store */
    return () => {
      viewChannel();
      dispatch(clearStoreAction());
    };
  }, [dispatch, viewChannel]);

  if (!loadedChannelId) {
    return <CollaborationLoader />;
  }

  return (
    <>
      <CollaborationMenu />
      <CollaborationUI.ContainerRight className={'collaboration-container'}>
        {/* Channel title and slack integration */}
        <CollaborationUI.ChannelHeader>
          <CollaborationUI.WorkspaceName>
            {getChannelNameById}
          </CollaborationUI.WorkspaceName>
        </CollaborationUI.ChannelHeader>
        {/* Chatting */}
        <CollaborationUI.ChatContent>
          <CollaborationChat />
        </CollaborationUI.ChatContent>
      </CollaborationUI.ContainerRight>
    </>
  );
});
